
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { ProfileAvatar } from "./ProfileAvatar";
import { ProfileUrlInput } from "./ProfileUrlInput";
import { GymPhotoUpload } from "./GymPhotoUpload";
import { TrainingTypesSelector } from "./TrainingTypesSelector";
import { ExperienceAndPricing } from "./ExperienceAndPricing";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useTranslations } from "@/hooks/useTranslations";

interface ProfileContentProps {
  userId: string;
  profile: {
    firstName: string;
    lastName: string;
    avatarUrl: string;
    publicUrl: string;
    gymPhotoUrl: string;
    trainingTypes: string[];
    aboutMe: string;
    specialties: string[];
    instagramUrl: string;
    facebookUrl: string;
    experienceYears: string;
    hourlyRate: string;
    showPrices: boolean;
  };
  loading: boolean;
  predefinedTypes: string[];
  onProfileChange: (updates: Partial<ProfileContentProps["profile"]>) => void;
  onSave: () => void;
}

export const ProfileContent = ({
  userId,
  profile,
  loading,
  predefinedTypes,
  onProfileChange,
  onSave
}: ProfileContentProps) => {
  const { t } = useTranslations("profile");
  
  return (
    <CardContent className="space-y-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
      <div className="space-y-4">
        <ProfileAvatar 
          userId={userId} 
          firstName={profile.firstName} 
          lastName={profile.lastName} 
          avatarUrl={profile.avatarUrl} 
          onAvatarUpdate={url => onProfileChange({
            avatarUrl: url
          })} 
        />

        <ProfileUrlInput 
          value={profile.publicUrl} 
          onChange={value => onProfileChange({
            publicUrl: value
          })} 
        />

        <GymPhotoUpload 
          userId={userId} 
          gymPhotoUrl={profile.gymPhotoUrl} 
          onGymPhotoUpdate={url => onProfileChange({
            gymPhotoUrl: url
          })} 
        />

        <div className="space-y-2">
          <Label htmlFor="aboutMe" className="text-slate-800 dark:text-gray-200">{t("aboutMe")}</Label>
          <Textarea 
            id="aboutMe" 
            value={profile.aboutMe} 
            onChange={e => onProfileChange({
              aboutMe: e.target.value
            })} 
            disabled={loading} 
            placeholder={t("aboutMePlaceholder")} 
            className="min-h-[100px] bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-900 dark:text-gray-100 rounded-xl" 
          />
        </div>

        <TrainingTypesSelector 
          trainingTypes={profile.trainingTypes} 
          predefinedTypes={predefinedTypes} 
          onTrainingTypesChange={types => onProfileChange({
            trainingTypes: types
          })} 
        />

        <ExperienceAndPricing 
          experienceYears={profile.experienceYears} 
          hourlyRate={profile.hourlyRate} 
          showPrices={profile.showPrices} 
          instagramUrl={profile.instagramUrl} 
          facebookUrl={profile.facebookUrl} 
          onExperienceYearsChange={value => onProfileChange({
            experienceYears: value
          })} 
          onHourlyRateChange={value => onProfileChange({
            hourlyRate: value
          })} 
          onShowPricesChange={value => onProfileChange({
            showPrices: value
          })} 
          onInstagramUrlChange={value => onProfileChange({
            instagramUrl: value
          })} 
          onFacebookUrlChange={value => onProfileChange({
            facebookUrl: value
          })} 
        />

        <div className="flex justify-end">
          <Button onClick={onSave} disabled={loading} className="rounded-xl">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("saveChanges")}
          </Button>
        </div>
      </div>
    </CardContent>
  );
};
